import React, { useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import Typography from '@tiptap/extension-typography';
import StarterKit from '@tiptap/starter-kit';
import clsx from 'clsx';

import { ABOUT_MAX_LENGTH } from 'constants/about';
import {
  getAboutPlainTextLength,
  normalizeAboutForStorage,
  sanitizeAboutHtml,
} from 'utils/aboutHtml';

import { AboutEditorToolbar } from './AboutEditorToolbar';
import classes from './TiptapAboutEditor.module.scss';

type Props = {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
};

export const TiptapAboutEditor: React.FC<Props> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Расскажите о себе, своем опыте и подходе к обучению...',
  disabled = false,
  hasError = false,
}) => {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // Tiptap v3 StarterKit already bundles Underline (separate extension caused a duplicate warning).
      }),
      Highlight.configure({ multicolor: false }),
      Subscript,
      Superscript,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right', 'justify'],
      }),
      Typography,
      Placeholder.configure({ placeholder }),
    ],
    [placeholder],
  );

  const editor = useEditor({
    extensions,
    content: value || '',
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: classes.editor,
        'data-placeholder': placeholder,
      },
      handleDOMEvents: {
        drop: (_view, event) => {
          if (event.dataTransfer?.files?.length) {
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
      handlePaste: (_view, event) => {
        if (event.clipboardData?.files?.length) {
          event.preventDefault();
          return true;
        }
        return false;
      },
      transformPastedHTML: (html) => sanitizeAboutHtml(html),
    },
    onUpdate: ({ editor: currentEditor }) => {
      const plainLength = currentEditor.getText().length;
      if (plainLength > ABOUT_MAX_LENGTH) {
        currentEditor.commands.undo();
        return;
      }
      onChange(normalizeAboutForStorage(currentEditor.getHTML()));
    },
    onBlur: () => {
      onBlur?.();
    },
  });

  useEffect(() => {
    if (!editor) return;
    const normalized = sanitizeAboutHtml(value || '');
    const current = normalizeAboutForStorage(editor.getHTML());
    if (normalized !== current) {
      editor.commands.setContent(normalized || '<p></p>', { emitUpdate: false });
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [editor, disabled]);

  const charCount = editor ? editor.getText().length : getAboutPlainTextLength(value);
  const isOverLimit = charCount > ABOUT_MAX_LENGTH;

  if (!editor) {
    return null;
  }

  return (
    <div className={classes.wrapper}>
      <div
        className={clsx(
          classes.shell,
          editor.isFocused && classes.shellFocused,
          hasError && classes.shellError,
          disabled && classes.shellDisabled,
        )}
      >
        <AboutEditorToolbar editor={editor} disabled={disabled} />
        <div className={classes.editorArea}>
          <EditorContent editor={editor} />
        </div>
      </div>

      <div className={clsx(classes.footer, isOverLimit && classes.footerOverLimit)}>
        {charCount} / {ABOUT_MAX_LENGTH}
      </div>
    </div>
  );
};
